import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  EyeIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { DateInput, DateDisplay } from '../../components/Forms';

interface VendorFilters {
  search: string;
  status: string;
}

interface Vendor {
  _id: string;
  companyName: string;
  name: string;
  isActive: boolean;
  contact: {
    email: string;
    phone: string;
  };
  subscription: {
    plan: string;
    endDate: string;
  };
}

interface VendorsResponse {
  data: Vendor[];
  pagination: {
    total: number;
    pages: number;
    currentPage: number;
  };
}

const Vendors: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<VendorFilters>({
    search: '',
    status: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedVendor, setSelectedVendor] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState<any>(null);
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    contact: {
      email: '',
      phone: '',
      mobile: '',
      website: ''
    },
    address: {
      street: '',
      district: '',
      city: '',
      postalCode: '',
      country: 'المملكة العربية السعودية'
    },
    license: {
      number: '',
      issuer: '',
      issueDate: '',
      expiryDate: ''
    },
    subscription: {
      plan: '',
      endDate: ''
    }
  });

  const { data: vendorsData, isLoading, error } = useQuery({
    queryKey: ['vendors', currentPage, filters],
    queryFn: () => apiService.getVendors(currentPage, 10, filters.search),
    placeholderData: (previousData) => previousData,
    enabled: !!(user && user.role === 'super_admin')
  });

  const { data: subscriptionPlans } = useQuery({
    queryKey: ['subscriptionPlans'],
    queryFn: () => apiService.getSubscriptionPlansAdmin(),
    enabled: !!(user && user.role === 'super_admin')
  });

  const deleteVendorMutation = useMutation({
    mutationFn: (vendorId: string) => apiService.deleteVendor(vendorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    }
  });

  const reactivateVendorMutation = useMutation({
    mutationFn: (vendorId: string) => apiService.reactivateVendor(vendorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    }
  });

  const createVendorMutation = useMutation({
    mutationFn: (vendorData: any) => apiService.createVendor(vendorData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      setShowAddModal(false);
      resetForm();
    }
  });

  const updateVendorMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => apiService.updateVendor(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      setShowEditModal(false);
      setEditingVendor(null);
      resetForm();
    }
  });

  // Only allow admin users to access this page
  if (!user || user.role !== 'super_admin') {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">غير مصرح</h3>
        <p className="text-gray-500">ليس لديك صلاحية للوصول لهذه الصفحة</p>
      </div>
    );
  }

  const handleSearch = (searchTerm: string) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
    setCurrentPage(1);
  };

  const handleStatusFilter = (status: string) => {
    setFilters(prev => ({ ...prev, status }));
    setCurrentPage(1);
  };

  const handleDeleteVendor = async (vendor: any) => {
    if (window.confirm(`هل أنت متأكد من حذف البائع "${vendor.companyName}"؟`)) {
      try {
        await deleteVendorMutation.mutateAsync(vendor._id);
      } catch (error) {
        console.error('Error deleting vendor:', error);
      }
    }
  };

  const handleReactivateVendor = async (vendor: any) => {
    try {
      await reactivateVendorMutation.mutateAsync(vendor._id);
    } catch (error) {
      console.error('Error reactivating vendor:', error);
    }
  };

  const handleViewDetails = (vendor: any) => {
    setSelectedVendor(vendor);
    setShowDetails(true);
  };

  const handleAddVendor = () => {
    setShowAddModal(true);
    resetForm();
  };

  const handleEditVendor = (vendor: any) => {
    setEditingVendor(vendor);
    setFormData({
      companyName: vendor.companyName || '',
      name: vendor.name || '',
      contact: {
        email: vendor.contact?.email || '',
        phone: vendor.contact?.phone || '',
        mobile: vendor.contact?.mobile || '',
        website: vendor.contact?.website || ''
      },
      address: {
        street: vendor.address?.street || '',
        district: vendor.address?.district || '',
        city: vendor.address?.city || '',
        postalCode: vendor.address?.postalCode || '',
        country: vendor.address?.country || 'المملكة العربية السعودية'
      },
      license: {
        number: vendor.license?.number || '',
        issuer: vendor.license?.issuer || '',
        issueDate: vendor.license?.issueDate ? new Date(vendor.license.issueDate).toISOString().split('T')[0] : '',
        expiryDate: vendor.license?.expiryDate ? new Date(vendor.license.expiryDate).toISOString().split('T')[0] : ''
      },
      subscription: {
        plan: vendor.subscription?.plan || 'basic',
        endDate: vendor.subscription?.endDate ? new Date(vendor.subscription.endDate).toISOString().split('T')[0] : ''
      }
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      companyName: '',
      name: '',
      contact: {
        email: '',
        phone: '',
        mobile: '',
        website: ''
      },
      address: {
        street: '',
        district: '',
        city: '',
        postalCode: '',
        country: 'المملكة العربية السعودية'
      },
      license: {
        number: '',
        issuer: '',
        issueDate: '',
        expiryDate: ''
      },
      subscription: {
        plan: subscriptionPlans?.[0]?.name || '',
        endDate: ''
      }
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingVendor) {
      updateVendorMutation.mutate({ id: editingVendor._id, data: formData });
    } else {
      createVendorMutation.mutate(formData);
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive 
          ? 'bg-green-100 text-green-800' 
          : 'bg-red-100 text-red-800'
      }`}>
        {isActive ? 'نشط' : 'غير نشط'}
      </span>
    );
  };

  const getPlanBadge = (plan: string) => {
    const planColors = {
      basic: 'bg-blue-100 text-blue-800',
      premium: 'bg-purple-100 text-purple-800',
      enterprise: 'bg-indigo-100 text-indigo-800'
    };

    const planLabels = {
      basic: 'أساسي',
      premium: 'مميز',
      enterprise: 'مؤسسات'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${planColors[plan as keyof typeof planColors] || 'bg-gray-100 text-gray-800'}`}>
        {planLabels[plan as keyof typeof planLabels] || plan}
      </span>
    );
  };

  const getPlanNameById = (planId: string) => {
    const plan = subscriptionPlans?.find((p: any) => p._id === planId);
    return plan ? plan.name : 'غير محدد';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">خطأ في تحميل البيانات</h3>
        <p className="text-gray-500">حدث خطأ أثناء تحميل قائمة البائعين</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">إدارة البائعين</h1>
          <p className="text-gray-600">إدارة البائعين والاشتراكات</p>
        </div>
        <button 
          className="btn-primary"
          onClick={handleAddVendor}
        >
          <PlusIcon className="h-5 w-5 ml-2" />
          إضافة بائع جديد
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              البحث
            </label>
            <input
              type="text"
              placeholder="البحث بالاسم أو الشركة..."
              className="input-field"
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              الحالة
            </label>
            <select
              className="input-field"
              value={filters.status}
              onChange={(e) => handleStatusFilter(e.target.value)}
            >
              <option value="">جميع الحالات</option>
              <option value="active">نشط</option>
              <option value="inactive">غير نشط</option>
            </select>
          </div>
        </div>
      </div>

      {/* Vendors Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  البائع
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الحالة
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الخطة
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  تاريخ الانتهاء
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {vendorsData?.data?.map((vendor: any) => (
                <tr key={vendor._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {vendor.companyName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {vendor.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(vendor.isActive)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getPlanBadge(vendor.subscription.plan)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <DateDisplay value={vendor.subscription.endDate} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2 space-x-reverse">
                      <button
                        onClick={() => handleViewDetails(vendor)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditVendor(vendor)}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      {vendor.isActive ? (
                        <button
                          onClick={() => handleDeleteVendor(vendor)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleReactivateVendor(vendor)}
                          className="text-green-600 hover:text-green-900"
                        >
                          <CheckIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {vendorsData?.pagination && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                السابق
              </button>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= vendorsData.pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                التالي
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  عرض <span className="font-medium">{(currentPage - 1) * 10 + 1}</span> إلى{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * 10, vendorsData.pagination.total)}
                  </span>{' '}
                  من <span className="font-medium">{vendorsData.pagination.total}</span> نتيجة
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    السابق
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= vendorsData.pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    التالي
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Vendor Details Modal */}
      {showDetails && selectedVendor && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                تفاصيل البائع
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 border-b pb-2">المعلومات الأساسية</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">اسم الشركة</label>
                    <p className="text-sm text-gray-900">{selectedVendor.companyName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">اسم البائع</label>
                    <p className="text-sm text-gray-900">{selectedVendor.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">معرف البائع</label>
                    <p className="text-sm text-gray-900">{selectedVendor.vendorId}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">الحالة</label>
                    <p className="text-sm text-gray-900">{getStatusBadge(selectedVendor.isActive)}</p>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 border-b pb-2">معلومات الاتصال</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">البريد الإلكتروني</label>
                    <p className="text-sm text-gray-900">{selectedVendor.contact?.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">الهاتف</label>
                    <p className="text-sm text-gray-900">{selectedVendor.contact?.phone}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">الجوال</label>
                    <p className="text-sm text-gray-900">{selectedVendor.contact?.mobile || 'غير محدد'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">الموقع الإلكتروني</label>
                    <p className="text-sm text-gray-900">{selectedVendor.contact?.website || 'غير محدد'}</p>
                  </div>
                </div>

                {/* Address Information */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 border-b pb-2">العنوان</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">الشارع</label>
                    <p className="text-sm text-gray-900">{selectedVendor.address?.street || 'غير محدد'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">الحي</label>
                    <p className="text-sm text-gray-900">{selectedVendor.address?.district || selectedVendor.address?.state || 'غير محدد'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">المدينة</label>
                    <p className="text-sm text-gray-900">{selectedVendor.address?.city}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">الرمز البريدي</label>
                    <p className="text-sm text-gray-900">{selectedVendor.address?.postalCode || 'غير محدد'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">البلد</label>
                    <p className="text-sm text-gray-900">{selectedVendor.address?.country}</p>
                  </div>
                </div>

                {/* License Information */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 border-b pb-2">معلومات الرخصة</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">رقم الرخصة</label>
                    <p className="text-sm text-gray-900">{selectedVendor.license?.number}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">الجهة المانحة</label>
                    <p className="text-sm text-gray-900">{selectedVendor.license?.issuer || selectedVendor.license?.issuingAuthority}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">تاريخ الإصدار</label>
                    <p className="text-sm text-gray-900">{selectedVendor.license?.issueDate ? <DateDisplay value={selectedVendor.license.issueDate} /> : 'غير محدد'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">تاريخ الانتهاء</label>
                    <p className="text-sm text-gray-900">{selectedVendor.license?.expiryDate ? <DateDisplay value={selectedVendor.license.expiryDate} /> : 'غير محدد'}</p>
                  </div>
                </div>

                {/* Subscription Information */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 border-b pb-2">معلومات الاشتراك</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">معرف الخطة</label>
                    <p className="text-sm text-gray-900">{selectedVendor.subscription?.planId}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">الخطة</label>
                    <p className="text-sm text-gray-900">{getPlanNameById(selectedVendor.subscription?.planId)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">تاريخ البداية</label>
                    <p className="text-sm text-gray-900">{selectedVendor.subscription?.startDate ? <DateDisplay value={selectedVendor.subscription.startDate} /> : 'غير محدد'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">تاريخ الانتهاء</label>
                    <p className="text-sm text-gray-900">{selectedVendor.subscription?.endDate ? <DateDisplay value={selectedVendor.subscription.endDate} /> : 'غير محدد'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">التجديد التلقائي</label>
                    <p className="text-sm text-gray-900">{selectedVendor.subscription?.autoRenew ? 'نعم' : 'لا'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">حالة الاشتراك</label>
                    <p className="text-sm text-gray-900">{selectedVendor.subscription?.isActive ? 'نشط' : 'غير نشط'}</p>
                  </div>
                </div>

                {/* System Information */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 border-b pb-2">معلومات النظام</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">اسم قاعدة البيانات</label>
                    <p className="text-sm text-gray-900">{selectedVendor.databaseName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">تاريخ الإنشاء</label>
                    <p className="text-sm text-gray-900">{selectedVendor.createdAt ? <DateDisplay value={selectedVendor.createdAt} showTime /> : 'غير محدد'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">آخر تحديث</label>
                    <p className="text-sm text-gray-900">{selectedVendor.updatedAt ? <DateDisplay value={selectedVendor.updatedAt} showTime /> : 'غير محدد'}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6 space-x-3 space-x-reverse">
                <button
                  onClick={() => setShowDetails(false)}
                  className="btn-secondary"
                >
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Vendor Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingVendor ? 'تعديل البائع' : 'إضافة بائع جديد'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      اسم الشركة <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                      className="input-field"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      اسم البائع <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="input-field"
                      required
                    />
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">معلومات الاتصال</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        البريد الإلكتروني <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={formData.contact.email}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          contact: { ...prev.contact, email: e.target.value }
                        }))}
                        className="input-field"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الهاتف <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={formData.contact.phone}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          contact: { ...prev.contact, phone: e.target.value }
                        }))}
                        className="input-field"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الجوال
                      </label>
                      <input
                        type="tel"
                        value={formData.contact.mobile}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          contact: { ...prev.contact, mobile: e.target.value }
                        }))}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الموقع الإلكتروني
                      </label>
                      <input
                        type="url"
                        value={formData.contact.website}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          contact: { ...prev.contact, website: e.target.value }
                        }))}
                        className="input-field"
                      />
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">العنوان</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الشارع
                      </label>
                      <input
                        type="text"
                        value={formData.address.street}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, street: e.target.value }
                        }))}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الحي
                      </label>
                      <input
                        type="text"
                        value={formData.address.district}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, district: e.target.value }
                        }))}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        المدينة <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.address.city}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, city: e.target.value }
                        }))}
                        className="input-field"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الرمز البريدي
                      </label>
                      <input
                        type="text"
                        value={formData.address.postalCode}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, postalCode: e.target.value }
                        }))}
                        className="input-field"
                      />
                    </div>
                  </div>
                </div>

                {/* License Information */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">معلومات الرخصة</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        رقم الرخصة <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.license.number}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          license: { ...prev.license, number: e.target.value }
                        }))}
                        className="input-field"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الجهة المانحة <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.license.issuer}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          license: { ...prev.license, issuer: e.target.value }
                        }))}
                        className="input-field"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        تاريخ الإصدار <span className="text-red-500">*</span>
                      </label>
                      <DateInput
                        value={formData.license.issueDate}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          license: { ...prev.license, issueDate: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        تاريخ الانتهاء <span className="text-red-500">*</span>
                      </label>
                      <DateInput
                        value={formData.license.expiryDate}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          license: { ...prev.license, expiryDate: e.target.value }
                        }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Subscription Information */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">معلومات الاشتراك</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        خطة الاشتراك <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.subscription.plan}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          subscription: { ...prev.subscription, plan: e.target.value }
                        }))}
                        className="input-field"
                        required
                      >
                        <option value="">اختر خطة الاشتراك</option>
                        {subscriptionPlans?.map((plan: any) => (
                          <option key={plan._id} value={plan.name}>
                            {plan.name} - {plan.price} ريال
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        تاريخ انتهاء الاشتراك <span className="text-red-500">*</span>
                      </label>
                      <DateInput
                        value={formData.subscription.endDate}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          subscription: { ...prev.subscription, endDate: e.target.value }
                        }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 space-x-reverse">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      setEditingVendor(null);
                      resetForm();
                    }}
                    className="btn-secondary"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    disabled={createVendorMutation.isPending || updateVendorMutation.isPending}
                    className="btn-primary"
                  >
                    {createVendorMutation.isPending || updateVendorMutation.isPending ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      editingVendor ? 'تحديث البائع' : 'إضافة البائع'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vendors; 